import React, { useState, useEffect, useRef } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { Bars3Icon } from "@heroicons/react/24/outline";
import {
    NavContainer,
    NavLogo,
    NavTool,
    MenuBtn,
    NavItemsWrapMD,
    NavItemsWrapXS,
    NavItems
} from "../layouts/Header";

import Dropdown from "./Dropdown";

const Nav = styled.nav.attrs({ id: "nav" })<{ active: any }>`
    ${tw`z-[999] w-full top-0 left-0 right-0 fixed  md:px-4   border-gray-200 `}

    ${props =>
        props.active
            ? tw`bg-transparent text-white`
            : tw`bg-white text-gray-800 border-b`}
`;

type HeaderProps = {
    className?: string;
};

const Header = ({ className = "" }: HeaderProps) => {
    const [menu, showMenu] = useState(false);
    const [solid, setSolid] = useState(false);
    const navRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const nav = navRef.current;
        if (!nav) return;

        const hero = nav.nextElementSibling;

        const update = () =>
            setSolid(
                hero
                    ? hero.getBoundingClientRect().bottom <= nav.offsetHeight
                    : true
            );

        update();
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, []);

    useEffect(() => {
        if (!menu) return;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") showMenu(false);
        };
        // md 이상에서는 햄버거 버튼 자체가 사라지므로 같이 닫는다.
        const onResize = () => {
            if (window.innerWidth >= 768) showMenu(false);
        };

        // iOS Safari 는 overflow:hidden 만으로 배경 스크롤이 막히지 않는다.
        // body 를 현재 위치에 고정했다가 닫을 때 그 자리로 되돌린다.
        const y = window.scrollY;
        const { style } = document.body;
        const prev = {
            position: style.position,
            top: style.top,
            left: style.left,
            right: style.right,
            overflow: style.overflow
        };

        style.position = "fixed";
        style.top = `-${y}px`;
        style.left = "0";
        style.right = "0";
        style.overflow = "hidden";

        window.addEventListener("keydown", onKey);
        window.addEventListener("resize", onResize);

        return () => {
            style.position = prev.position;
            style.top = prev.top;
            style.left = prev.left;
            style.right = prev.right;
            style.overflow = prev.overflow;
            // 전역 scroll-behavior: smooth 를 타면 복귀가 애니메이션으로 돈다.
            window.scrollTo({ top: y, behavior: "instant" as ScrollBehavior });
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("resize", onResize);
        };
    }, [menu]);

    return (
        <>
            {/* 헤더(z-999)보다 한 단 아래. sticky 탭바(z-40)·지원바(z-30)를 덮는다. */}
            <div
                aria-hidden="true"
                onClick={() => showMenu(false)}
                className={`fixed inset-0 z-[998] bg-black/50 transition-opacity duration-200 md:hidden ${
                    menu ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
            />

            <Nav
                ref={navRef}
                className={className}
                active={!solid && !menu ? true : null}
            >
                <NavContainer>
                    <NavLogo href="/">
                        <img
                            src="/img/logo.svg"
                            className="h-8"
                            alt="company Logo"
                        />
                    </NavLogo>
                    <NavTool>
                        <MenuBtn onClick={() => showMenu(!menu)}>
                            <Bars3Icon className="h-6 w-6" />
                        </MenuBtn>
                    </NavTool>

                    {/* md 이상에서만 보인다 */}
                    <NavItemsWrapMD>
                        <NavItems>
                            <Dropdown />
                        </NavItems>
                    </NavItemsWrapMD>

                    {/* md 미만에서 메뉴 버튼을 눌렀을 때만 보인다 */}
                    {menu && (
                        <NavItemsWrapXS>
                            <NavItems>
                                <Dropdown />
                            </NavItems>
                        </NavItemsWrapXS>
                    )}
                </NavContainer>
            </Nav>
        </>
    );
};

export default Header;
